import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { 
  Menu, X, Phone, Mail, MapPin, Clock, 
  User, LogOut, ChevronDown, Car, Bike,
  Facebook, Instagram, Youtube, ShoppingCart, Bell
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_atelier-carrosserie/artifacts/r5w9lm54_file_1769113425476.jpeg";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Layout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout, token } = useAuth();
  const { getCartCount, openCart } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const cartCount = getCartCount();
  const [alertsCount, setAlertsCount] = useState(0);

  // Compte comptable : verrouille sur /comptabilite et /connexion uniquement.
  // Empeche les reservations RDV, achats boutique, paiements client, etc.
  useEffect(() => {
    if (user?.role === 'accountant') {
      const allowed = ['/comptabilite', '/connexion', '/deconnexion'];
      if (!allowed.some((p) => location.pathname.startsWith(p))) {
        navigate('/comptabilite', { replace: true });
      }
    }
  }, [user, location.pathname, navigate]);

  // Poll alerts (unread quotes / invoices) for connected clients.
  useEffect(() => {
    if (!user || !token) { setAlertsCount(0); return; }
    if (user.role && user.role !== 'client') { setAlertsCount(0); return; }
    let cancelled = false;
    const fetchAlerts = async () => {
      try {
        const res = await axios.get(`${API}/user/alerts`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!cancelled) setAlertsCount(res.data?.unread_count || 0);
      } catch (_) { /* silent */ }
    };
    fetchAlerts();
    const iv = setInterval(fetchAlerts, 60000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [user, token, location.pathname]);

  const navLinks = [
    { name: 'Accueil', href: '/' },
    { name: 'Services', href: '/services' },
    { name: 'Tarifs Lavage', href: '/tarifs-lavage' },
    { name: 'Mobilité', href: '/mobilite' },
    { name: 'Promotions', href: '/promotions' },
    { name: 'Contact', href: '/contact' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (href) => location.pathname === href;

  return (
    <div className="min-h-screen flex flex-col bg-[#09090B]">
      {/* Top Bar */}
      <div className="bg-[#18181B] border-b border-zinc-800 py-2 hidden lg:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center text-sm">
          <div className="flex items-center gap-6 text-zinc-400">
            <a href="tel:0805032032" className="flex items-center gap-2 hover:text-red-500 transition-colors">
              <Phone size={14} />
              <span>0805 032 032</span>
            </a>
            <a href="mailto:info@lehubatelier.fr" className="flex items-center gap-2 hover:text-red-500 transition-colors">
              <Mail size={14} />
              <span>info@lehubatelier.fr</span>
            </a>
          </div>
          <div className="flex items-center gap-6 text-zinc-400">
            <span className="flex items-center gap-2">
              <Clock size={14} />
              <span>Lun-Sam: 8h00-19h30</span>
            </span>
            <div className="flex items-center gap-3">
              <a href="#" className="hover:text-red-500 transition-colors"><Facebook size={16} /></a>
              <a href="#" className="hover:text-red-500 transition-colors"><Instagram size={16} /></a>
              <a href="#" className="hover:text-red-500 transition-colors"><Youtube size={16} /></a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className="glass sticky top-0 z-50 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Le Hub Atelier" className="h-12 w-auto" />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`px-4 py-2 text-sm font-medium transition-colors rounded-md ${
                    isActive(link.href)
                      ? 'text-red-500 bg-red-500/10'
                      : 'text-zinc-300 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </nav>

            {/* Cart & Auth Section */}
            <div className="hidden lg:flex items-center gap-4">
              {/* Cart Button */}
              <button
                onClick={openCart}
                className="relative p-2 text-zinc-300 hover:text-white hover:bg-white/5 rounded-md transition-colors"
                data-testid="cart-btn"
              >
                <ShoppingCart size={22} />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full font-bold">
                    {cartCount > 9 ? '9+' : cartCount}
                  </span>
                )}
              </button>
              
              {/* Boutique Link */}
              <Link to="/boutique">
                <Button variant="outline" className="border-red-600/50 text-red-400 hover:bg-red-600/10" data-testid="shop-btn">
                  Boutique
                </Button>
              </Link>
              
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="relative flex items-center gap-2 text-zinc-300" data-testid="user-menu-btn">
                      <User size={18} />
                      <span>{user.name}</span>
                      {alertsCount > 0 && (
                        <span
                          className="absolute -top-1 -right-1 flex items-center gap-1 bg-red-600 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 animate-pulse"
                          data-testid="user-alerts-badge"
                          title={`${alertsCount} document(s) non lu(s)`}
                        >
                          <Bell size={10} />
                          {alertsCount > 9 ? '9+' : alertsCount}
                        </span>
                      )}
                      <ChevronDown size={16} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800">
                    <DropdownMenuItem onClick={() => navigate('/mon-compte')} className="cursor-pointer">
                      Mon Compte
                    </DropdownMenuItem>
                    {user.role === 'admin' && (
                      <DropdownMenuItem onClick={() => navigate('/admin')} className="cursor-pointer">
                        Administration
                      </DropdownMenuItem>
                    )}
                    {user.role === 'collaborator' && (
                      <DropdownMenuItem onClick={() => navigate('/admin')} className="cursor-pointer">
                        Espace Collaborateur
                      </DropdownMenuItem>
                    )}
                    {user.role === 'accountant' && (
                      <DropdownMenuItem onClick={() => navigate('/comptabilite')} className="cursor-pointer" data-testid="menu-comptabilite">
                        Comptabilite
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-500">
                      <LogOut size={16} className="mr-2" />
                      Déconnexion
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <>
                  <Link to="/connexion">
                    <Button variant="ghost" className="text-zinc-300" data-testid="login-btn">
                      Connexion
                    </Button>
                  </Link>
                  <Link to="/inscription">
                    <Button className="bg-red-600 hover:bg-red-700 text-white btn-glow" data-testid="register-btn">
                      Inscription
                    </Button>
                  </Link>
                </>
              )}
            </div>

            {/* Mobile Menu Button + Cart */}
            <div className="lg:hidden flex items-center gap-2">
              <button
                onClick={openCart}
                className="relative p-2 text-zinc-300"
                data-testid="mobile-cart-btn"
              >
                <ShoppingCart size={22} />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full font-bold">
                    {cartCount > 9 ? '9+' : cartCount}
                  </span>
                )}
              </button>
              <button
                className="p-2 text-zinc-300"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-zinc-800 bg-[#09090B]">
            <nav className="px-4 py-4 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-4 py-3 rounded-md text-sm font-medium ${
                    isActive(link.href)
                      ? 'text-red-500 bg-red-500/10'
                      : 'text-zinc-300 hover:bg-white/5'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <Link
                to="/boutique"
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-4 py-3 rounded-md text-sm font-medium flex items-center gap-2 ${
                  isActive('/boutique')
                    ? 'text-red-500 bg-red-500/10'
                    : 'text-zinc-300 hover:bg-white/5'
                }`}
              >
                <ShoppingCart size={16} />
                Boutique
              </Link>
              <div className="pt-4 border-t border-zinc-800 space-y-2">
                {user ? (
                  <>
                    <Link
                      to="/mon-compte"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-3 rounded-md text-sm text-zinc-300"
                    >
                      Mon Compte ({user.loyalty_points} pts)
                    </Link>
                    {user.role === 'admin' && (
                      <Link
                        to="/admin"
                        onClick={() => setMobileMenuOpen(false)}
                        className="block px-4 py-3 rounded-md text-sm text-zinc-300"
                      >
                        Administration
                      </Link>
                    )}
                    {user.role === 'collaborator' && (
                      <Link
                        to="/admin"
                        onClick={() => setMobileMenuOpen(false)}
                        className="block px-4 py-3 rounded-md text-sm text-zinc-300"
                      >
                        Espace Collaborateur
                      </Link>
                    )}
                    <button
                      onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                      className="block w-full text-left px-4 py-3 rounded-md text-sm text-red-500"
                    >
                      Déconnexion
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/connexion"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-3 rounded-md text-sm text-zinc-300"
                    >
                      Connexion
                    </Link>
                    <Link
                      to="/inscription"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-3 rounded-md text-sm bg-red-600 text-white text-center"
                    >
                      Inscription
                    </Link>
                  </>
                )}
              </div>
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-[#0a0a0c] border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Brand */}
            <div>
              <img src={LOGO_URL} alt="Le Hub Atelier" className="h-12 mb-4" />
              <p className="text-zinc-400 text-sm">
                Votre garage multi-services à Paris 16e. Auto et Moto, tous types de véhicules.
              </p>
              <div className="flex items-center gap-3 mt-4">
                <Car className="text-red-500" size={24} />
                <Bike className="text-red-500" size={24} />
              </div>
            </div>

            {/* Services */}
            <div>
              <h4 className="text-white font-bold mb-4">Nos Services</h4>
              <ul className="space-y-2 text-zinc-400 text-sm">
                <li><Link to="/services" className="hover:text-red-500 transition-colors">Lavage Auto/Moto</Link></li>
                <li><Link to="/services" className="hover:text-red-500 transition-colors">Mécanique</Link></li>
                <li><Link to="/services" className="hover:text-red-500 transition-colors">Carrosserie</Link></li>
                <li><Link to="/services" className="hover:text-red-500 transition-colors">Contrôle Technique</Link></li>
              </ul>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-white font-bold mb-4">Liens Rapides</h4>
              <ul className="space-y-2 text-zinc-400 text-sm">
                <li><Link to="/reservation" className="hover:text-red-500 transition-colors">Prendre RDV</Link></li>
                <li><Link to="/devis" className="hover:text-red-500 transition-colors">Demander un Devis</Link></li>
                <li><Link to="/suivi" className="hover:text-red-500 transition-colors">Suivre ma Réparation</Link></li>
                <li><Link to="/tarifs-lavage" className="hover:text-red-500 transition-colors">Tarifs Lavage</Link></li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-white font-bold mb-4">Contact</h4>
              <ul className="space-y-3 text-zinc-400 text-sm">
                <li className="flex items-start gap-2">
                  <MapPin size={18} className="text-red-500 mt-0.5 flex-shrink-0" />
                  <span>142 boulevard Murat<br />75016 Paris</span>
                </li>
                <li className="flex items-center gap-2">
                  <Phone size={18} className="text-red-500" />
                  <a href="tel:0805032032" className="hover:text-red-500">0805 032 032</a>
                </li>
                <li className="flex items-center gap-2">
                  <Mail size={18} className="text-red-500" />
                  <a href="mailto:info@lehubatelier.fr" className="hover:text-red-500">info@lehubatelier.fr</a>
                </li>
                <li className="flex items-center gap-2">
                  <Clock size={18} className="text-red-500" />
                  <span>Lun-Sam: 8h00-19h30</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-zinc-800 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-zinc-500 text-sm">
              © {new Date().getFullYear()} Le Hub Atelier Auto Moto. Tous droits réservés.
            </p>
            <p className="text-zinc-500 text-sm flex items-center gap-2">
              <span className="text-green-500">●</span> Agréé Assurances
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
